import React, { useEffect, useState } from 'react';
import { userService, authService, adminService, systemService, productUpdateService, type ProductUpdate } from '../services/api';
import {
    User, Shield, Key, Plus, Loader2, RefreshCw, Database, Wifi, Clock,
    CheckCircle2, XCircle, AlertTriangle, Zap, FileText, ArrowRightLeft,
    Lock, Mail, Pencil, Eye, EyeOff, Search, BarChart3, UserX, UserCheck,
    Activity, ChevronDown, ChevronRight, Info, GraduationCap, Plug, Workflow,
    Trash2, ListTree, Wallet, Trophy
} from 'lucide-react';
import { DetectionThresholdsTab } from '../components/settings/DetectionThresholdsTab';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { LEGAL_POLICY_VERSION } from '../legal/policyVersion';
import { clearLegalLocalState, readLegalAcceptance } from '../legal/acceptance';
import { resetTour, requestTourOpen } from '../components/PlatformTour';
import { useConfirmDialog } from '../components/DialogHost';
import { clearSession } from '../utils/session';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { cn } from '../utils/cn';
import { isFeatureAllowed, useFeature } from '../auth/features';
import { SourcesTab } from '../components/settings/SourcesTab';
import { CloudVisionSetting } from '../components/settings/CloudVisionSetting';
import { LlmCostTab } from '../components/settings/LlmCostTab';
import { ScoreboardTab } from '../components/settings/ScoreboardTab';

// ─── Role definitions ────────────────────────────────────────────
// What a role may *see* comes from the served feature matrix (useFeature);
// this helper only mirrors the backend's role-assignment rule below.
const isSuperAdmin = (role?: string) => role === 'super_admin';

// Roles the current admin is allowed to assign. Backend enforces the same:
// super_admin may assign any role; a plain admin may assign officer/analyst/trial
// (and may promote any unowned trial user — SSO self-signups have no owner).
const assignableRoles = (currentRole?: string): string[] =>
    isSuperAdmin(currentRole)
        ? ['super_admin', 'admin', 'officer', 'analyst', 'trial']
        : ['officer', 'analyst', 'trial'];

const ROLE_INFO: Record<string, { label: string; color: string; description: string; permissions: string[] }> = {
    super_admin: {
        label: 'Super Administrator',
        color: 'text-foreground',
        description: 'Highest privilege level. Full system access including user management, role escalation, and system configuration.',
        permissions: [
            'Create, disable, and manage all user accounts',
            'Assign any role including admin and super_admin',
            'View all user activity logs and statistics',
            'Trigger data ingestion and demo seeding',
            'Access all investigations, cases, and reports',
            'System-wide configuration and pipeline management',
        ],
    },
    admin: {
        label: 'Administrator',
        color: 'text-foreground',
        description: 'Full system access. Manages users, system settings, and data pipelines.',
        permissions: [
            'Create, disable, and manage user accounts',
            'View all user activity logs and statistics',
            'Trigger data ingestion and demo seeding',
            'Access all investigations, cases, and reports',
            'Change user roles and permissions',
        ],
    },
    officer: {
        label: 'Investigation Officer',
        color: 'text-foreground',
        description: 'Full investigator access — create cases, run investigations, manage evidence. Cannot manage users or system settings.',
        permissions: [
            'Create and manage cases and investigations',
            'Run auto-investigations and trace addresses',
            'Generate LE reports and export data',
            'Flag addresses and manage watchlists',
            'Submit feedback and view own search history',
        ],
    },
    trial: {
        label: 'Trial',
        color: 'text-muted-foreground',
        description: 'Default for new self-signup (SSO) accounts. Workbench triage and tracing, own cases and reports. No auto-investigation, OSINT, MuleHunter, Telegram, P2P or off-ramp intelligence.',
        permissions: [
            'Triage and trace addresses in the Workbench',
            'Create and manage own cases',
            'View LE reports and export own cases',
            'Use the demos and submit feedback',
        ],
    },
    analyst: {
        label: 'Analyst',
        color: 'text-foreground',
        description: 'Full investigator access — create cases, run investigations, manage evidence. Cannot manage users or system settings.',
        permissions: [
            'Create and manage cases and investigations',
            'Run auto-investigations and trace addresses',
            'Generate LE reports and export data',
            'Flag addresses and manage watchlists',
            'Submit feedback and view own search history',
        ],
    },
};

// Row shape returned by GET /admin/activity-log (super-admin audit trail).
interface ActivityLogRow {
    id: number;
    user_id: number | null;
    username: string | null;
    activity_type: string | null;
    resource_type: string | null;
    resource_id: string | null;
    action: string | null;
    status_code: number | null;
    ip_address: string | null;
    created_at: string;
}

export const Settings: React.FC = () => {
    // Served by /auth/me (auth/features.ts) — the same matrix the API enforces.
    const canManageUsers = useFeature('admin.users');
    const canAdminTools = useFeature('admin.tools');
    const canCloudVision = useFeature('settings.cloud_vision');
    const canLlmCost = useFeature('settings.llm_cost');
    const isSuper = useFeature('admin.super');
    const canReviewSources = useFeature('scrapers');
    const navigate = useNavigate();
    const { confirm, confirmDialog } = useConfirmDialog();
    const [activeTab, setActiveTab] = useState<'settings' | 'connections' | 'providers' | 'sources' | 'activity' | 'thresholds' | 'llmcost' | 'scoreboard' | 'whatsnew'>('settings');

    // Providers tab (super-admin only)
    const [providerUsage, setProviderUsage] = useState<any | null>(null);
    const [providerUsageLoading, setProviderUsageLoading] = useState(false);
    const [sanctionsCoverage, setSanctionsCoverage] = useState<any | null>(null);
    const [sanctionsCoverageLoading, setSanctionsCoverageLoading] = useState(false);
    const [arkhamSaving, setArkhamSaving] = useState(false);
    const [providerSort, setProviderSort] = useState<'today_calls' | 'free_tier_pct' | 'name'>('today_calls');
    const [providerFilter, setProviderFilter] = useState<'all' | 'active' | 'with_calls_today'>('all');
    const [cascadeChain, setCascadeChain] = useState('ETH');
    const [cascadeCapability, setCascadeCapability] = useState('entity');
    const [cascadeResult, setCascadeResult] = useState<any | null>(null);
    // Per-row Test button: in-flight provider name + last result keyed by name.
    const [testingProvider, setTestingProvider] = useState<string | null>(null);
    const [testResults, setTestResults] = useState<Record<string, any>>({});
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [actionLoading, setActionLoading] = useState('');

    // Connections state
    const [connections, setConnections] = useState<any[]>([]);
    const [connSummary, setConnSummary] = useState<any>(null);
    const [connLoading, setConnLoading] = useState(false);
    const [activeLLM, setActiveLLM] = useState('');
    const [lastChecked, setLastChecked] = useState<Date | null>(null);

    // Profile editing
    const [editingProfile, setEditingProfile] = useState(false);
    const [profileForm, setProfileForm] = useState({ full_name: '', email: '', digest_recipients: '' });

    // Password change (OTP flow)
    const [pwStep, setPwStep] = useState<'idle' | 'otp_sent' | 'verified'>('idle');
    const [otpCode, setOtpCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [pwLoading, setPwLoading] = useState(false);
    const [eraseConfirm, setEraseConfirm] = useState('');
    const [erasing, setErasing] = useState(false);
    const [exportingData, setExportingData] = useState(false);
    const [explainerMode, setExplainerMode] = useState<'on' | 'off' | 'detailed'>(() => {
        const v = localStorage.getItem('explainerMode');
        if (v === 'off' || v === 'detailed') return v;
        return 'on';
    });

    // Admin: expanded user activity
    const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
    const [userActivity, setUserActivity] = useState<any[]>([]);
    const [activityLoading, setActivityLoading] = useState(false);
    const [digestEmail, setDigestEmail] = useState('');
    const [digestSending, setDigestSending] = useState(false);

    // Admin: reset another user's password
    const [resetPwUser, setResetPwUser] = useState<{ id: number; username: string } | null>(null);
    const [resetPwValue, setResetPwValue] = useState('');
    const [resetPwConfirm, setResetPwConfirm] = useState('');
    const [resetPwShow, setResetPwShow] = useState(false);
    const [resetPwSaving, setResetPwSaving] = useState(false);

    // Super-admin: cross-user activity audit (Client Activity tab)
    const [activityRows, setActivityRows] = useState<ActivityLogRow[]>([]);
    const [activityLogLoading, setActivityLogLoading] = useState(false);
    const [activityWindow, setActivityWindow] = useState<number>(60); // since_minutes
    const [activityEndpoint, setActivityEndpoint] = useState('');
    const [activityUserFilter, setActivityUserFilter] = useState('');

    // Product updates / version newsletter (What's New)
    const [productUpdates, setProductUpdates] = useState<ProductUpdate[]>([]);
    const [productUpdatesLoading, setProductUpdatesLoading] = useState(false);
    const [newsletterTo, setNewsletterTo] = useState('saurabh@outris.com');
    const [updateSaving, setUpdateSaving] = useState(false);
    const [newUpdateForm, setNewUpdateForm] = useState({
        title: '',
        summary: '',
        version_label: '',
        category: 'feature',
        bulletsText: '',
        publishAndEmail: true,
    });

    // New User Form
    const [newUser, setNewUser] = useState({
        username: '',
        password: '',
        email: '',
        full_name: '',
        role: 'officer'
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const me = await authService.getMe();
            setCurrentUser(me);
            setProfileForm({ full_name: me.full_name || '', email: me.email || '', digest_recipients: me.digest_recipients || '' });

            if (isFeatureAllowed(me.features, me.role, 'admin.users')) {
                try {
                    const u = await userService.getAllWithStats();
                    setUsers(u);
                } catch {
                    // Fallback to basic list if stats endpoint not available
                    const u = await userService.getAll();
                    setUsers(u);
                }
            }
        } catch (error) {
            console.error("Failed to load settings data", error);
        } finally {
            setLoading(false);
        }
    };

    const loadConnections = async () => {
        setConnLoading(true);
        try {
            const data = await systemService.getConnections();
            setConnections(data.connections || []);
            setConnSummary(data.summary || null);
            setActiveLLM(data.active_llm_provider || '');
            setLastChecked(new Date());
        } catch (error) {
            console.error("Failed to load connections", error);
        } finally {
            setConnLoading(false);
        }
    };

    useEffect(() => {
        if (!currentUser) return;
        const admin = canAdminTools;
        const superA = isSuper;
        if (activeTab === 'connections' && !admin) setActiveTab('settings');
        if (activeTab === 'providers' && !superA) setActiveTab('settings');
        if (activeTab === 'activity' && !superA) setActiveTab('settings');
        if (activeTab === 'sources' && !superA && !canReviewSources) setActiveTab('settings');
        if (activeTab === 'llmcost' && !canLlmCost) setActiveTab('settings');
        if (activeTab === 'scoreboard' && !superA) setActiveTab('settings');
    }, [activeTab, currentUser, canAdminTools, isSuper, canReviewSources, canLlmCost]);

    useEffect(() => {
        if (activeTab === 'connections' && canAdminTools && connections.length === 0) {
            loadConnections();
        }
    }, [activeTab, currentUser?.role, canAdminTools]);

    const setExplainerModePref = (mode: 'on' | 'off' | 'detailed') => {
        setExplainerMode(mode);
        localStorage.setItem('explainerMode', mode);
        window.dispatchEvent(new StorageEvent('storage', { key: 'explainerMode', newValue: mode }));
        toast.success(
            mode === 'off'
                ? 'Explain Mode off — feature tips stay dismissed'
                : mode === 'detailed'
                    ? 'Explain Mode: detailed tips restored'
                    : 'Explain Mode on — feature tips shown'
        );
    };

    // Client Activity tab (super-admin): load on open + whenever the time window changes.
    useEffect(() => {
        if (activeTab === 'activity' && isSuper) {
            loadActivityLog();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, activityWindow, currentUser?.role, isSuper]);

    // What's New tab: load product updates when opened.
    useEffect(() => {
        if (activeTab === 'whatsnew') {
            loadProductUpdates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, currentUser?.role]);

    const loadProductUpdates = async () => {
        setProductUpdatesLoading(true);
        try {
            const includeDrafts = canAdminTools;
            const data = await productUpdateService.list(includeDrafts, 50);
            setProductUpdates(data.updates || []);
            if (data.newsletter_to) setNewsletterTo(data.newsletter_to);
        } catch (e: any) {
            toast.error(e?.response?.data?.detail || 'Failed to load product updates');
        } finally {
            setProductUpdatesLoading(false);
        }
    };

    const parseBulletsFromText = (text: string) =>
        text
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => {
                const sep = line.indexOf(':');
                if (sep > 0 && sep < 80) {
                    return { label: line.slice(0, sep).trim(), text: line.slice(sep + 1).trim() };
                }
                return { label: '', text: line };
            });

    const handleCreateProductUpdate = async () => {
        if (!newUpdateForm.title.trim()) {
            toast.error('Title is required');
            return;
        }
        setUpdateSaving(true);
        try {
            const created = await productUpdateService.create({
                title: newUpdateForm.title.trim(),
                summary: newUpdateForm.summary.trim(),
                version_label: newUpdateForm.version_label.trim() || undefined,
                category: newUpdateForm.category,
                bullets: parseBulletsFromText(newUpdateForm.bulletsText),
            });
            if (newUpdateForm.publishAndEmail) {
                const result = await productUpdateService.publish(created.id, {
                    send_email: true,
                    force_email: false,
                });
                const emailOk = result.email?.success;
                if (emailOk) {
                    toast.success(`Published and emailed to ${result.email?.recipient || newsletterTo}`);
                } else if (result.email && !result.email.success) {
                    toast.warning(
                        `Published, but email failed: ${result.email.message || 'unknown error'}`,
                    );
                } else {
                    toast.success('Published');
                }
            } else {
                toast.success('Draft saved — publish when ready');
            }
            setNewUpdateForm({
                title: '',
                summary: '',
                version_label: '',
                category: 'feature',
                bulletsText: '',
                publishAndEmail: true,
            });
            await loadProductUpdates();
        } catch (e: any) {
            toast.error(e?.response?.data?.detail || 'Failed to save product update');
        } finally {
            setUpdateSaving(false);
        }
    };

    const handlePublishUpdate = async (id: number) => {
        setUpdateSaving(true);
        try {
            const result = await productUpdateService.publish(id, { send_email: true });
            if (result.email?.success) {
                toast.success(`Published and emailed to ${result.email.recipient || newsletterTo}`);
            } else if (result.email && !result.email.success) {
                toast.warning(`Published, but email failed: ${result.email.message || 'error'}`);
            } else {
                toast.success('Published');
            }
            await loadProductUpdates();
        } catch (e: any) {
            toast.error(e?.response?.data?.detail || 'Publish failed');
        } finally {
            setUpdateSaving(false);
        }
    };

    const handleResendUpdateEmail = async (id: number) => {
        setUpdateSaving(true);
        try {
            const result = await productUpdateService.send(id, { force: true });
            if (result.email?.success) {
                toast.success(`Emailed to ${result.email.recipient || newsletterTo}`);
            } else {
                toast.error(result.email?.message || 'Email failed');
            }
            await loadProductUpdates();
        } catch (e: any) {
            toast.error(e?.response?.data?.detail || 'Email send failed');
        } finally {
            setUpdateSaving(false);
        }
    };

    const handleDeleteUpdate = async (id: number, title: string) => {
        const ok = await confirm({
            title: 'Delete product update?',
            message: `Remove “${title}” from the changelog? This cannot be undone.`,
            confirmText: 'Delete',
            danger: true,
        });
        if (!ok) return;
        setUpdateSaving(true);
        try {
            await productUpdateService.remove(id);
            toast.success('Update deleted');
            await loadProductUpdates();
        } catch (e: any) {
            toast.error(e?.response?.data?.detail || 'Delete failed');
        } finally {
            setUpdateSaving(false);
        }
    };

    const categoryBadge = (category: string) => {
        const c = (category || 'feature').toLowerCase();
        if (c === 'quality' || c === 'fix') {
            return 'bg-emerald-600/20 text-emerald-400 border-emerald-600/30';
        }
        if (c === 'security') {
            return 'bg-red-600/20 text-red-400 border-red-600/30';
        }
        return 'bg-primary/10 text-primary border-primary/20';
    };

    const categoryLabel = (category: string) => {
        const c = (category || 'feature').toLowerCase();
        if (c === 'quality') return 'Quality & Stability';
        if (c === 'fix') return 'Bug Fix';
        if (c === 'security') return 'Security';
        return 'New Feature';
    };

    const formatReleased = (iso: string | null) => {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        } catch {
            return iso.slice(0, 10);
        }
    };

    // ── Providers tab ────────────────────────────────────────────────
    const loadProviderUsage = async () => {
        setProviderUsageLoading(true);
        try {
            // 30 days of history so the table can show today / 7d / 30d aggregates
            const data = await adminService.getProviderUsage(30);
            setProviderUsage(data);
        } catch (error: any) {
            console.error('Failed to load provider usage', error);
            toast.error(error?.response?.data?.detail || 'Failed to load provider usage');
        } finally {
            setProviderUsageLoading(false);
        }
    };

    const loadSanctionsCoverage = async () => {
        setSanctionsCoverageLoading(true);
        try {
            const data = await adminService.getSanctionsCoverage();
            setSanctionsCoverage(data);
        } catch (error: any) {
            console.error('Failed to load sanctions coverage', error);
        } finally {
            setSanctionsCoverageLoading(false);
        }
    };

    const testProvider = async (name: string) => {
        setTestingProvider(name);
        try {
            const result = await adminService.testProvider(name);
            setTestResults((prev) => ({ ...prev, [name]: result }));
            if (result?.ok) {
                toast.success(`${name}: OK in ${result.duration_ms} ms`);
            } else if (result?.error === 'no_probe_implemented') {
                toast(`${name}: no probe implemented yet`, { icon: 'ℹ️' });
            } else if (result?.result?.skipped) {
                toast(`${name}: ${result.result.skipped}`, { icon: '⚠️' });
            } else {
                toast.error(`${name}: ${result?.error || result?.result?.summary || 'failed'}`);
            }
        } catch (error: any) {
            console.error('Provider probe failed', error);
            toast.error(error?.response?.data?.detail || 'Provider probe failed');
            setTestResults((prev) => ({
                ...prev,
                [name]: { ok: false, error: error?.message || 'request_failed' },
            }));
        } finally {
            setTestingProvider(null);
        }
    };

    const resetProviderCircuit = async (name: string) => {
        try {
            const result = await adminService.resetProviderCircuit(name);
            if (result?.was_open) {
                toast.success(`Circuit breaker reset for ${name}`);
            } else {
                toast.success(`${name} breaker was already closed`);
            }
            loadProviderUsage();
        } catch (error: any) {
            console.error('Failed to reset circuit breaker', error);
            toast.error(error?.response?.data?.detail || 'Failed to reset circuit breaker');
        }
    };

    const handleArkhamControl = async (patch: {
        enabled?: boolean | null;
        transfers_enabled?: boolean | null;
        clear_enabled_override?: boolean;
        clear_transfers_override?: boolean;
        reset_credit_estimate?: boolean;
    }) => {
        setArkhamSaving(true);
        try {
            await adminService.setArkhamControls(patch);
            toast.success('Arkham controls updated');
            await loadProviderUsage();
        } catch (error: any) {
            console.error('Failed to update Arkham controls', error);
            toast.error(error?.response?.data?.detail || 'Failed to update Arkham controls');
        } finally {
            setArkhamSaving(false);
        }
    };

    const loadCascade = async (chain: string, capability: string) => {
        try {
            const data = await adminService.getProviderCascade(chain, capability);
            setCascadeResult(data);
        } catch (error: any) {
            console.error('Failed to load cascade', error);
            toast.error(error?.response?.data?.detail || 'Cascade lookup failed');
        }
    };

    useEffect(() => {
        if (activeTab === 'providers' && isSuper) {
            if (!providerUsage) loadProviderUsage();
            if (!sanctionsCoverage) loadSanctionsCoverage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, currentUser?.role]);

    useEffect(() => {
        if (activeTab === 'providers' && isSuper) {
            loadCascade(cascadeChain, cascadeCapability);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cascadeChain, cascadeCapability, activeTab]);

    // ─── Profile actions ─────────────────────────────────────────
    const handleSaveProfile = async () => {
        setActionLoading('profile');
        try {
            await authService.updateProfile(profileForm);
            toast.success('Profile updated');
            setEditingProfile(false);
            loadData();
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || 'Failed to update profile');
        } finally {
            setActionLoading('');
        }
    };

    const handleExportMyData = async () => {
        setExportingData(true);
        try {
            const payload = await authService.exportMyData();
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'cryptoflow-data-export.json';
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Account export downloaded');
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || 'Could not export account data');
        } finally {
            setExportingData(false);
        }
    };

    const handleErasePersonalData = async () => {
        if (eraseConfirm.trim() !== 'DELETE MY DATA') {
            toast.error('Type DELETE MY DATA to confirm');
            return;
        }
        setErasing(true);
        try {
            await authService.erasePersonalData('DELETE MY DATA');
            toast.success('Personal data deleted from the database');
            clearLegalLocalState();
            clearSession();
            navigate('/login', { replace: true });
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || 'Could not delete personal data');
        } finally {
            setErasing(false);
        }
    };

    // ─── Password change OTP flow ────────────────────────────────
    const handleRequestOtp = async () => {
        setPwLoading(true);
        try {
            const res = await authService.requestOtp('password_change');
            toast.success(res.message);
            setPwStep('otp_sent');
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || 'Failed to send verification code');
        } finally {
            setPwLoading(false);
        }
    };

    const handleChangePassword = async () => {
        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        setPwLoading(true);
        try {
            await authService.changePassword(otpCode, newPassword);
            toast.success('Password changed successfully');
            setPwStep('idle');
            setOtpCode('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || 'Failed to change password');
        } finally {
            setPwLoading(false);
        }
    };

    // ─── Admin user actions ──────────────────────────────────────
    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await userService.create({
                ...newUser,
                full_name: newUser.full_name.trim() || undefined,
            });
            setShowModal(false);
            setNewUser({ username: '', password: '', email: '', full_name: '', role: 'officer' });
            loadData();
            toast.success('User created');
        } catch (error) {
            toast.error('Failed to create user. Check if username exists.');
        }
    };

    const handleToggleActive = async (userId: number, username: string, currentlyActive: boolean) => {
        const action = currentlyActive ? 'disable' : 'enable';
        const shouldProceed = await confirm({
            title: `${currentlyActive ? 'Disable' : 'Enable'} @${username}?`,
            message: currentlyActive
                ? 'This user will no longer be able to log in.'
                : 'This user will regain access to the platform.',
            confirmText: currentlyActive ? 'Disable' : 'Enable',
            danger: currentlyActive,
        });
        if (!shouldProceed) return;
        try {
            await userService.toggleActive(userId);
            toast.success(`User @${username} ${action}d`);
            loadData();
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || `Failed to ${action} user`);
        }
    };

    const handleUpdateRole = async (userId: number, username: string, newRole: string) => {
        try {
            await userService.updateRole(userId, newRole);
            toast.success(`@${username} role changed to ${ROLE_INFO[newRole]?.label || newRole}`);
            loadData();
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || 'Failed to update role');
            loadData(); // re-sync the select back to the real value
        }
    };

    const handleDeleteUser = async (userId: number, username: string) => {
        const shouldProceed = await confirm({
            title: `Delete @${username}?`,
            message: 'This deactivates the account. The user will no longer be able to log in.',
            confirmText: 'Delete',
            danger: true,
        });
        if (!shouldProceed) return;
        try {
            await userService.deleteUser(userId);
            toast.success(`User @${username} deleted`);
            loadData();
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || 'Failed to delete user');
        }
    };

    const openResetPassword = (userId: number, username: string) => {
        setResetPwUser({ id: userId, username });
        setResetPwValue('');
        setResetPwConfirm('');
        setResetPwShow(false);
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!resetPwUser) return;
        if (resetPwValue.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        if (resetPwValue !== resetPwConfirm) {
            toast.error('Passwords do not match');
            return;
        }
        setResetPwSaving(true);
        try {
            await userService.resetPassword(resetPwUser.id, resetPwValue);
            toast.success(`Password reset for @${resetPwUser.username}`);
            setResetPwUser(null);
            setResetPwValue('');
            setResetPwConfirm('');
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || 'Failed to reset password');
        } finally {
            setResetPwSaving(false);
        }
    };

    const loadActivityLog = async () => {
        setActivityLogLoading(true);
        try {
            const params: { limit?: number; since_minutes?: number; user_id?: number; endpoint_like?: string } = {
                limit: 500,
                since_minutes: activityWindow,
            };
            if (activityEndpoint.trim()) params.endpoint_like = activityEndpoint.trim();
            const uid = parseInt(activityUserFilter, 10);
            if (!isNaN(uid)) params.user_id = uid;
            const data = await adminService.getActivityLog(params);
            setActivityRows(data?.rows || []);
        } catch (err: any) {
            console.error('Failed to load activity log', err);
            toast.error(err?.response?.data?.detail || 'Failed to load activity log');
            setActivityRows([]);
        } finally {
            setActivityLogLoading(false);
        }
    };

    const handleLoadActivity = async (userId: number) => {
        if (expandedUserId === userId) {
            setExpandedUserId(null);
            return;
        }
        setExpandedUserId(userId);
        setActivityLoading(true);
        try {
            const data = await userService.getActivity(userId, 30);
            setUserActivity(data);
        } catch {
            setUserActivity([]);
        } finally {
            setActivityLoading(false);
        }
    };

    const handleSeedDemo = async () => {
        const shouldSeed = await confirm({
            title: 'Re-seed demo data?',
            message: 'This will wipe existing demo data (entities, p2p) and re-seed it.',
            confirmText: 'Re-seed',
            danger: true,
        });
        if (!shouldSeed) return;
        setActionLoading('seed');
        try {
            const res = await adminService.seedDemo();
            toast.success(res.message || 'Demo data re-seeded');
        } catch (error) {
            console.error(error);
            toast.error('Failed to trigger seeding.');
        } finally {
            setActionLoading('');
        }
    };

    const handleIngestion = async () => {
        setActionLoading('ingest');
        try {
            const res = await adminService.triggerIngestion();
            if (res.results) {
                const r = res.results;
                toast.success(`Ingestion complete: OFAC ${r.ofac_count}, ScamDB ${r.scam_count}, Scrapers ${r.new_added}, DB Sync ${r.db_synced || 0}`, { duration: 7000 });
            } else {
                toast.success(res.message || 'Ingestion triggered');
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to trigger ingestion.');
        } finally {
            setActionLoading('');
        }
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div>;

    // ─── Status helpers ──────────────────────────────────────────
    const statusIcon = (s: string) => {
        if (s === 'ok') return <CheckCircle2 className="w-5 h-5 text-emerald-400" />;
        if (s === 'no_key') return <AlertTriangle className="w-5 h-5 text-amber-400" />;
        return <XCircle className="w-5 h-5 text-red-400" />;
    };
    const statusBg = (s: string) => {
        if (s === 'ok') return 'border-emerald-500/30 bg-emerald-500/5';
        if (s === 'no_key') return 'border-amber-500/30 bg-amber-500/5';
        return 'border-red-500/30 bg-red-500/5';
    };
    const statusLabel = (s: string) => {
        if (s === 'ok') return <span className="text-xs font-semibold px-2 py-0.5 rounded bg-emerald-400/10 text-emerald-400 border border-emerald-400/30">LIVE</span>;
        if (s === 'no_key') return <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-400/10 text-amber-400 border border-amber-400/30">NO KEY</span>;
        return <span className="text-xs font-semibold px-2 py-0.5 rounded bg-red-400/10 text-red-400 border border-red-400/30">DOWN</span>;
    };
    const categoryIcon = (cat: string) => {
        if (cat === 'database') return <Database className="w-4 h-4 text-primary" />;
        if (cat === 'blockchain') return <Zap className="w-4 h-4 text-primary" />;
        if (cat === 'ai') return <Wifi className="w-4 h-4 text-cyan-400" />;
        return <Wifi className="w-4 h-4 text-muted-foreground" />;
    };

    const roleInfo = ROLE_INFO[currentUser?.role] || ROLE_INFO.officer;

    return (
        <div className={`p-6 max-w-7xl mx-auto space-y-6`}>
            <div>
                <h1 className="text-2xl font-bold text-foreground">Account &amp; Settings</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Profile, legal, preferences, and {canManageUsers ? 'admin tools' : 'account options'}.
                </p>
            </div>

            {/* Tab bar */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 border-b border-border pb-0 overflow-x-auto">
                <button
                    onClick={() => setActiveTab('settings')}
                    className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${activeTab === 'settings' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                >
                    <span className="flex items-center gap-2"><Shield className="w-4 h-4" /> Settings</span>
                </button>
                {canManageUsers && (
                    <button
                        onClick={() => setActiveTab('connections')}
                        className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${activeTab === 'connections' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    >
                        <span className="flex items-center gap-2">
                            <Wifi className="w-4 h-4" /> Connections
                            {connSummary && (
                                <span className="text-xs font-medium px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground border border-border">
                                    {connSummary.ok}/{connSummary.total}
                                </span>
                            )}
                        </span>
                    </button>
                )}
                {isSuper && (
                    <button
                        onClick={() => setActiveTab('providers')}
                        className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${activeTab === 'providers' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    >
                        <span className="flex items-center gap-2">
                            <Plug className="w-4 h-4" /> Providers
                        </span>
                    </button>
                )}
                {(isSuper || canReviewSources) && (
                    <button
                        onClick={() => setActiveTab('sources')}
                        className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${activeTab === 'sources' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    >
                        <span className="flex items-center gap-2">
                            <Database className="w-4 h-4" /> Sources
                        </span>
                    </button>
                )}
                {isSuper && (
                    <button
                        onClick={() => setActiveTab('activity')}
                        className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${activeTab === 'activity' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    >
                        <span className="flex items-center gap-2">
                            <ListTree className="w-4 h-4" /> Client Activity
                        </span>
                    </button>
                )}
                <button
                    onClick={() => setActiveTab('thresholds')}
                    data-tour="settings-thresholds-tab"
                    className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${activeTab === 'thresholds' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                >
                    <span className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" /> Detection thresholds
                    </span>
                </button>
                {canLlmCost && (
                    <button
                        onClick={() => setActiveTab('llmcost')}
                        data-tour="settings-llm-cost-tab"
                        className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${activeTab === 'llmcost' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    >
                        <span className="flex items-center gap-2">
                            <Wallet className="w-4 h-4" /> Model cost
                        </span>
                    </button>
                )}
                {isSuper && (
                    <button
                        onClick={() => setActiveTab('scoreboard')}
                        data-tour="settings-scoreboard"
                        className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${activeTab === 'scoreboard' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    >
                        <span className="flex items-center gap-2">
                            <Trophy className="w-4 h-4" /> Scoreboard
                        </span>
                    </button>
                )}
                <button
                    onClick={() => setActiveTab('whatsnew')}
                    className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${activeTab === 'whatsnew' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                >
                    <span className="flex items-center gap-2">
                        <Zap className="w-4 h-4" /> What's New
                    </span>
                </button>
            </div>

            {/* ═══════════════════ CONNECTIONS TAB ═══════════════════ */}
            {activeTab === 'connections' && canAdminTools && (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                                <Wifi className="w-5 h-5 text-primary" /> Service Connections
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                Live health status of all external APIs, databases, and integrations.
                                {activeLLM && <> Active LLM: <span className="text-cyan-400 font-medium">{activeLLM}</span></>}
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            {lastChecked && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> {lastChecked.toLocaleTimeString()}
                                </span>
                            )}
                            <button
                                onClick={loadConnections}
                                disabled={connLoading}
                                className="px-3 py-1.5 bg-primary/10 text-primary border border-primary/30 rounded-lg hover:bg-primary/20 transition-colors flex items-center gap-2 text-sm"
                            >
                                {connLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                {connLoading ? 'Checking...' : 'Refresh'}
                            </button>
                        </div>
                    </div>

                    {connSummary && (
                        <div className="grid grid-cols-3 gap-4">
                            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
                                <div className="text-3xl font-bold text-emerald-400">{connSummary.ok}</div>
                                <div className="text-xs text-emerald-400/70 uppercase tracking-wider mt-1">Connected</div>
                            </div>
                            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                                <div className="text-3xl font-bold text-red-400">{connSummary.error}</div>
                                <div className="text-xs text-red-400/70 uppercase tracking-wider mt-1">Errors</div>
                            </div>
                            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
                                <div className="text-3xl font-bold text-amber-400">{connSummary.no_key}</div>
                                <div className="text-xs text-amber-400/70 uppercase tracking-wider mt-1">No Key</div>
                            </div>
                        </div>
                    )}

                    {connLoading && connections.length === 0 ? (
                        <div className="flex items-center justify-center py-16 text-muted-foreground">
                            <Loader2 className="w-6 h-6 animate-spin mr-3" /> Probing services...
                        </div>
                    ) : (
                        <>
                            {['database', 'blockchain', 'ai', 'scraper'].map(category => {
                                const catConns = connections.filter(c => c.category === category);
                                if (catConns.length === 0) return null;
                                const catLabel: Record<string, string> = {
                                    database: 'Databases',
                                    blockchain: 'Blockchain APIs',
                                    ai: 'AI / LLM',
                                    scraper: 'Scrapers & Integrations'
                                };
                                return (
                                    <div key={category}>
                                        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                                            {categoryIcon(category)} {catLabel[category] || category}
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                            {catConns.map((conn: any, i: number) => (
                                                <div key={i} className={`border rounded-xl p-4 ${statusBg(conn.status)} transition-colors`}>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center gap-2">
                                                            {statusIcon(conn.status)}
                                                            <span className="font-semibold text-foreground text-sm">{conn.name}</span>
                                                        </div>
                                                        {statusLabel(conn.status)}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground space-y-1">
                                                        <div className="flex items-center justify-between">
                                                            <span>{conn.detail}</span>
                                                            {conn.latency_ms > 0 && (
                                                                <span className="text-muted-foreground font-mono">{conn.latency_ms}ms</span>
                                                            )}
                                                        </div>
                                                        {conn.key_hint && (
                                                            <div className="text-muted-foreground font-mono text-[10px]">
                                                                Key: {conn.key_hint}
                                                            </div>
                                                        )}
                                                        {/* Crystal auto-enrich toggle */}
                                                        {conn.auto_enrich !== undefined && conn.status === 'ok' && (
                                                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
                                                                <span className="text-[11px] text-foreground/70">Auto-enrich in investigations</span>
                                                                <button
                                                                    onClick={async () => {
                                                                        try {
                                                                            const resp = await systemService.toggleCrystalAuto(!conn.auto_enrich);
                                                                            toast.success(resp.message);
                                                                            loadConnections();
                                                                        } catch (err: any) {
                                                                            toast.error(err?.response?.data?.detail || 'Failed to toggle');
                                                                        }
                                                                    }}
                                                                    className={`px-2 py-0.5 text-[10px] font-semibold rounded transition-colors ${
                                                                        conn.auto_enrich
                                                                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                                                                            : 'bg-secondary text-muted-foreground border border-border'
                                                                    }`}
                                                                >
                                                                    {conn.auto_enrich ? 'ON' : 'OFF'}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            )}

            {/* ═══════════════════ SETTINGS TAB ═══════════════════ */}
            {activeTab === 'settings' && (
                <div className="space-y-8">
                    <h1 className="text-2xl font-bold text-foreground">Account & Settings</h1>

                    {/* ── My Profile ── */}
                    <div className="bg-secondary/20 border border-border rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold flex items-center gap-2">
                                <User className="w-5 h-5 text-primary" /> My Profile
                            </h2>
                            {!editingProfile && (
                                <button
                                    onClick={() => setEditingProfile(true)}
                                    className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                                >
                                    <Pencil className="w-3.5 h-3.5" /> Edit
                                </button>
                            )}
                        </div>

                        {editingProfile ? (
                            <div className="space-y-4 max-w-lg">
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Full Name</label>
                                    <input
                                        type="text"
                                        className="w-full bg-card border border-border/80 rounded-lg p-2.5 text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                                        value={profileForm.full_name}
                                        onChange={e => setProfileForm({ ...profileForm, full_name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Email</label>
                                    <input
                                        type="email"
                                        className="w-full bg-card border border-border/80 rounded-lg p-2.5 text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                                        value={profileForm.email}
                                        onChange={e => setProfileForm({ ...profileForm, email: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Daily Digest Recipients</label>
                                    <input
                                        type="text"
                                        placeholder="officer2@agency.gov.in; supervisor@agency.gov.in"
                                        className="w-full bg-card border border-border/80 rounded-lg p-2.5 text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                                        value={profileForm.digest_recipients}
                                        onChange={e => setProfileForm({ ...profileForm, digest_recipients: e.target.value })}
                                    />
                                    <p className="text-[11px] text-muted-foreground/80 mt-1">Optional. Leave empty unless someone else must receive your daily digest. We do not collect extra addresses by default.</p>
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={handleSaveProfile}
                                        disabled={actionLoading === 'profile'}
                                        className="bg-primary hover:bg-brand-gold-hover text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                                    >
                                        {actionLoading === 'profile' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                        Save
                                    </button>
                                    <button
                                        onClick={() => {
                                            setEditingProfile(false);
                                            setProfileForm({ full_name: currentUser?.full_name || '', email: currentUser?.email || '', digest_recipients: currentUser?.digest_recipients || '' });
                                        }}
                                        className="text-muted-foreground hover:text-foreground px-4 py-2 text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-4 bg-card rounded-lg border border-border">
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Full Name</span>
                                    <p className="font-medium text-lg">{currentUser?.full_name || 'N/A'}</p>
                                </div>
                                <div className="p-4 bg-card rounded-lg border border-border">
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Username</span>
                                    <p className="font-medium text-lg font-mono">{currentUser?.username}</p>
                                </div>
                                <div className="p-4 bg-card rounded-lg border border-border">
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Email</span>
                                    <p className="font-medium text-lg">{currentUser?.email || 'N/A'}</p>
                                </div>
                                <div className="p-4 bg-card rounded-lg border border-border">
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Last Login</span>
                                    <p className="font-medium text-lg">
                                        {currentUser?.last_login
                                            ? new Date(currentUser.last_login).toLocaleString()
                                            : 'N/A'}
                                    </p>
                                </div>
                                <div className="p-4 bg-card rounded-lg border border-border md:col-span-2">
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Digest recipients</span>
                                    <p className="font-medium text-lg">
                                        {currentUser?.digest_recipients?.trim()
                                            ? currentUser.digest_recipients
                                            : 'None (digest goes to your account email only)'}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Legal ── */}
                    <div className="bg-secondary/20 border border-border rounded-xl p-6">
                        <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary" /> Terms and privacy
                        </h2>
                        <p className="text-sm text-muted-foreground mb-4">
                            These are the documents you accepted at sign-in. They describe how officer accounts,
                            activity logs, and case data are handled.
                        </p>
                        <div className="flex flex-wrap gap-3 text-sm">
                            <Link to="/terms" className="text-primary hover:underline">Terms of Use</Link>
                            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                        </div>
                        <p className="mt-3 text-xs text-muted-foreground">
                            Current version {LEGAL_POLICY_VERSION}
                            {readLegalAcceptance()?.acceptedAt
                                ? ` · accepted ${new Date(readLegalAcceptance()!.acceptedAt).toLocaleString()}`
                                : ''}
                        </p>
                        <div className="mt-4 space-y-2">
                            <p className="text-sm text-muted-foreground">
                                <span className="font-medium text-foreground">Export my account data</span> downloads a
                                JSON file with your profile fields and last legal acceptance (DPDP access / DSAR-style).
                                It does not include case files, investigations, or evidence — those stay with the agency.
                            </p>
                            <button
                                type="button"
                                onClick={handleExportMyData}
                                disabled={exportingData}
                                className="px-4 py-2 border border-border rounded-lg text-sm disabled:opacity-50"
                                data-testid="export-my-data"
                            >
                                {exportingData ? 'Exporting…' : 'Export my account data'}
                            </button>
                        </div>
                    </div>

                    <div className="bg-secondary/20 border border-destructive/40 rounded-xl p-6">
                        <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-destructive" /> Delete my personal data
                        </h2>
                        <p className="text-sm text-muted-foreground mb-4">
                            Irreversible DPDP erasure for <span className="font-medium text-foreground">your person</span>,
                            not the agency&apos;s case archive. Removes name, email, password, digest list, search history,
                            notifications, OTP rows, and legal-acceptance rows; anonymises feedback and strips IP/UA from
                            your activity log. Watchlist alert emails are cleared. Cases, investigations, and evidence
                            stay. Your login is deactivated (username becomes a tombstone). Type{' '}
                            <span className="font-mono text-foreground">DELETE MY DATA</span> to confirm.
                        </p>
                        <input
                            type="text"
                            className="w-full max-w-md bg-card border border-border rounded-lg p-2.5 text-foreground mb-3"
                            placeholder="Type DELETE MY DATA"
                            value={eraseConfirm}
                            onChange={(e) => setEraseConfirm(e.target.value)}
                            data-testid="erase-confirm-input"
                        />
                        <button
                            type="button"
                            onClick={handleErasePersonalData}
                            disabled={erasing || eraseConfirm.trim() !== 'DELETE MY DATA'}
                            className="px-4 py-2 bg-destructive/15 text-destructive border border-destructive/40 rounded-lg text-sm disabled:opacity-50"
                            data-testid="erase-personal-data"
                        >
                            {erasing ? 'Deleting…' : 'Delete from database'}
                        </button>
                    </div>

                    {/* ── Cloud vision for image intake (admin tiers, OD27) ── */}
                    {canCloudVision && <CloudVisionSetting />}

                    {/* ── Role & Permissions ── */}
                    <div className="bg-secondary/20 border border-border rounded-xl p-6">
                        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <Shield className="w-5 h-5 text-primary" /> Your Role & Permissions
                        </h2>
                        <div className="flex items-start gap-4">
                            <div className={`px-3 py-1.5 rounded-lg text-sm font-semibold border bg-muted text-foreground border-border`}>
                                {roleInfo.label}
                            </div>
                            <div className="flex-1">
                                <p className="text-muted-foreground text-sm mb-3">{roleInfo.description}</p>
                                <ul className="space-y-1.5">
                                    {roleInfo.permissions.map((p, i) => (
                                        <li key={i} className="text-sm text-foreground/80 flex items-start gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        {!canManageUsers && (
                            <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1">
                                <Info className="w-3 h-3" /> Need different permissions? Contact your administrator.
                            </p>
                        )}
                    </div>

                    {/* ── Platform Tour ── */}
                    <div className="bg-secondary/20 border border-border rounded-xl p-4 flex items-center justify-between">
                        <div>
                            <h2 className="text-sm font-semibold flex items-center gap-2">
                                <GraduationCap className="w-4 h-4 text-primary" /> Platform Tour
                            </h2>
                            <p className="text-xs text-muted-foreground mt-0.5">Guided walkthrough of key features</p>
                        </div>
                        <button
                            onClick={() => {
                                resetTour();
                                navigate('/');
                                requestAnimationFrame(() => requestTourOpen());
                                toast.success('Platform tour restarted');
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors"
                        >
                            Retake Tour
                        </button>
                    </div>

                    {/* ── Explain Mode ── */}
                    <div className="bg-secondary/20 border border-border rounded-xl p-4 space-y-3">
                        <div>
                            <h2 className="text-sm font-semibold flex items-center gap-2">
                                <Info className="w-4 h-4 text-primary" /> Explain Mode
                            </h2>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Controls in-app feature tips (Workbench / Inspector). Restore tips after dismissing them.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2" role="group" aria-label="Explain Mode">
                            {([
                                { value: 'on' as const, label: 'On' },
                                { value: 'detailed' as const, label: 'Detailed' },
                                { value: 'off' as const, label: 'Off' },
                            ]).map(opt => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => setExplainerModePref(opt.value)}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                        explainerMode === opt.value
                                            ? 'bg-primary text-primary-foreground border-primary'
                                            : 'bg-card text-muted-foreground border-border hover:bg-muted'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ── Change Password ── */}
                    <div className="bg-secondary/20 border border-border rounded-xl p-6">
                        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <Lock className="w-5 h-5 text-amber-400" /> Change Password
                        </h2>

                        {pwStep === 'idle' && (
                            <div>
                                <p className="text-sm text-muted-foreground mb-4">
                                    To change your password, we'll send a one-time verification code to your registered email
                                    {currentUser?.email ? ` (${currentUser.email})` : ''}.
                                </p>
                                <button
                                    onClick={handleRequestOtp}
                                    disabled={pwLoading || !currentUser?.email}
                                    className="px-4 py-2 bg-amber-600/20 text-amber-400 border border-amber-600/50 rounded-lg hover:bg-amber-600/30 transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
                                >
                                    {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                                    Send Verification Code
                                </button>
                                {!currentUser?.email && (
                                    <p className="text-xs text-red-400 mt-2">No email on file. Add an email address above first.</p>
                                )}
                            </div>
                        )}

                        {pwStep === 'otp_sent' && (
                            <div className="space-y-4 max-w-md">
                                <p className="text-sm text-muted-foreground">
                                    Enter the 6-digit code sent to your email, then choose a new password.
                                </p>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Verification Code</label>
                                    <input
                                        type="text"
                                        maxLength={6}
                                        placeholder="000000"
                                        className="w-full bg-card border border-border/80 rounded-lg p-2.5 text-foreground text-center text-xl tracking-[0.5em] font-mono focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                                        value={otpCode}
                                        onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">New Password</label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            minLength={6}
                                            className="w-full bg-card border border-border/80 rounded-lg p-2.5 text-foreground pr-10 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                                            value={newPassword}
                                            onChange={e => setNewPassword(e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Confirm Password</label>
                                    <input
                                        type="password"
                                        className="w-full bg-card border border-border/80 rounded-lg p-2.5 text-foreground focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                    />
                                    {confirmPassword && newPassword !== confirmPassword && (
                                        <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                                    )}
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={handleChangePassword}
                                        disabled={pwLoading || otpCode.length !== 6 || newPassword.length < 6 || newPassword !== confirmPassword}
                                        className="bg-amber-600 hover:bg-amber-500 text-foreground px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                                        Change Password
                                    </button>
                                    <button
                                        onClick={() => { setPwStep('idle'); setOtpCode(''); setNewPassword(''); setConfirmPassword(''); }}
                                        className="text-muted-foreground hover:text-foreground px-4 py-2 text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <button
                                    onClick={handleRequestOtp}
                                    disabled={pwLoading}
                                    className="text-xs text-muted-foreground hover:text-foreground/80"
                                >
                                    Didn't receive code? Resend
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ── System Maintenance (admins only) ── */}
                    {canManageUsers && (
                    <div className="bg-secondary/20 border border-border rounded-xl p-6">
                        <h2 className="text-lg font-semibold flex items-center gap-2 mb-6">
                            <Database className="w-5 h-5 text-primary" /> System Maintenance & Data
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="p-5 bg-card/50 border border-border rounded-xl">
                                <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                                    <RefreshCw className="w-4 h-4 text-muted-foreground" /> Demo Data Seeding
                                </h3>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Force re-seed the database with demo data (Tornado Cash, P2P Scams, WazirX).
                                    <b> Warn:</b> This may reset entities and cases.
                                </p>
                                <button
                                    onClick={handleSeedDemo}
                                    disabled={!!actionLoading}
                                    className="px-4 py-2 bg-muted text-foreground border border-border rounded-lg hover:bg-muted/80 transition-colors flex items-center gap-2"
                                >
                                    {actionLoading === 'seed' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                                    Re-Seed Demo Data
                                </button>
                            </div>

                            <div className="p-5 bg-card/50 border border-border rounded-xl">
                                <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-primary" /> Threat Intelligence Ingestion
                                </h3>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Manually trigger the daily ingestion pipeline (OFAC, ScamDB, Phishing Feeds).
                                </p>
                                <button
                                    onClick={handleIngestion}
                                    disabled={!!actionLoading}
                                    className="px-4 py-2 bg-primary/10 text-primary border border-primary/30 rounded-lg hover:bg-primary/20 transition-colors flex items-center gap-2"
                                >
                                    {actionLoading === 'ingest' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                    Trigger Ingestion
                                </button>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* ═══════════════════ ADMIN: USER MANAGEMENT ═══════════════════ */}
                    {canManageUsers && (
                        <div className="rounded-xl border border-border bg-card/40 p-5 shadow-sm shadow-black/5 sm:p-6">
                            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                                        <Key className="h-5 w-5 text-primary" /> User Management
                                    </h2>
                                    <p className="mt-1 text-sm text-muted-foreground">
                                        Roles, account status, and usage for officers on this deployment.
                                    </p>
                                </div>
                                <Button type="button" size="sm" onClick={() => setShowModal(true)} className="shrink-0">
                                    <Plus className="h-4 w-4" /> Add User
                                </Button>
                            </div>

                            {/* Compact role reference */}
                            <div className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                                {Object.entries(ROLE_INFO).map(([key, info]) => (
                                    <div key={key} className="rounded-lg border border-border/80 bg-background/50 px-3 py-2.5">
                                        <span className={cn('text-xs font-semibold', info.color)}>{info.label}</span>
                                        <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-muted-foreground">{info.description}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="space-y-3">
                                {users.length === 0 && (
                                    <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                                        No users yet. Add an officer account to get started.
                                    </div>
                                )}
                                {users.map((u) => {
                                    const displayName = u.full_name || u.username;
                                    const initials = String(displayName || '?')
                                        .split(/\s+/)
                                        .filter(Boolean)
                                        .slice(0, 2)
                                        .map((p: string) => p[0]?.toUpperCase() ?? '')
                                        .join('') || '?';
                                    const lastActive = u.last_activity
                                        ? new Date(u.last_activity).toLocaleString()
                                        : u.last_login
                                            ? new Date(u.last_login).toLocaleString()
                                            : 'Never';
                                    const metrics = [
                                        { label: 'Searches', value: u.search_count, icon: Search },
                                        { label: 'API calls', value: u.api_call_count, icon: BarChart3 },
                                        { label: 'Investigations', value: u.investigation_count, icon: Search },
                                        { label: 'Cases', value: u.case_count, icon: FileText },
                                    ] as const;
                                    const expanded = expandedUserId === u.id;
                                    const isSelf = u.id === currentUser?.id;

                                    return (
                                        <div
                                            key={u.id}
                                            className={cn(
                                                'rounded-xl border bg-background/70 transition-colors',
                                                expanded ? 'border-primary/40 shadow-sm shadow-black/5' : 'border-border hover:border-border/90 hover:bg-background',
                                                !u.is_active && 'opacity-80',
                                            )}
                                        >
                                            <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between">
                                                <div className="flex min-w-0 items-start gap-3">
                                                    <div
                                                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-secondary/60 text-xs font-semibold text-foreground"
                                                        aria-hidden
                                                    >
                                                        {initials}
                                                    </div>
                                                    <div className="min-w-0 space-y-1.5">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <p className="truncate text-sm font-semibold text-foreground">{displayName}</p>
                                                            <span
                                                                className={cn(
                                                                    'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                                                                    u.is_active
                                                                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                                                        : 'border-red-500/30 bg-red-500/10 text-red-500',
                                                                )}
                                                            >
                                                                {u.is_active ? 'Active' : 'Disabled'}
                                                            </span>
                                                        </div>
                                                        <p className="truncate text-xs text-muted-foreground">
                                                            @{u.username}
                                                            {u.email ? ` · ${u.email}` : ''}
                                                        </p>
                                                        <div className="flex flex-wrap items-center gap-2 pt-0.5">
                                                            {isSelf ? (
                                                                <span className="text-xs font-medium text-foreground/80">
                                                                    {ROLE_INFO[u.role]?.label || u.role}
                                                                </span>
                                                            ) : (
                                                                <select
                                                                    value={u.role}
                                                                    onChange={(e) => handleUpdateRole(u.id, u.username, e.target.value)}
                                                                    className="h-8 rounded-lg border border-input bg-background px-2.5 text-xs font-medium text-foreground shadow-sm shadow-black/5 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/20"
                                                                    title="Change role"
                                                                >
                                                                    {Array.from(new Set([u.role, ...assignableRoles(currentUser?.role)])).map((r) => (
                                                                        <option key={r} value={r}>{ROLE_INFO[r]?.label || r}</option>
                                                                    ))}
                                                                </select>
                                                            )}
                                                            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                                                                <Clock className="h-3 w-3 shrink-0" />
                                                                {lastActive}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:shrink-0">
                                                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                                                        {metrics.map(({ label, value, icon: Icon }) => (
                                                            <div
                                                                key={label}
                                                                className="min-w-[4.5rem] rounded-lg border border-border/70 bg-secondary/20 px-2.5 py-1.5"
                                                            >
                                                                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                                                    <Icon className="h-3 w-3 shrink-0" />
                                                                    <span className="truncate">{label}</span>
                                                                </div>
                                                                <p className="mt-0.5 font-mono text-sm font-semibold tabular-nums text-foreground">
                                                                    {value ?? '—'}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="flex items-center gap-1 self-end sm:self-center">
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="icon"
                                                            className="h-8 w-8"
                                                            onClick={() => handleLoadActivity(u.id)}
                                                            title="View activity"
                                                            aria-expanded={expanded}
                                                            aria-label={`View activity for ${u.username}`}
                                                        >
                                                            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="icon"
                                                            className="h-8 w-8"
                                                            onClick={() => handleToggleActive(u.id, u.username, u.is_active)}
                                                            title={u.is_active ? 'Disable user' : 'Enable user'}
                                                            disabled={isSelf}
                                                            aria-label={u.is_active ? `Disable ${u.username}` : `Enable ${u.username}`}
                                                        >
                                                            {u.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="icon"
                                                            className="h-8 w-8"
                                                            onClick={() => openResetPassword(u.id, u.username)}
                                                            title="Reset password"
                                                            aria-label={`Reset password for ${u.username}`}
                                                        >
                                                            <Key className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="icon"
                                                            className="h-8 w-8 text-destructive hover:text-destructive"
                                                            onClick={() => handleDeleteUser(u.id, u.username)}
                                                            title="Delete (deactivate) user"
                                                            disabled={isSelf}
                                                            aria-label={`Delete ${u.username}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>

                                            {expanded && (
                                                <div className="space-y-4 border-t border-border bg-secondary/15 px-4 py-3">
                                                    <div className="rounded-lg border border-border/60 bg-background/60 p-3">
                                                        <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                                                            <Mail className="h-3.5 w-3.5" /> Daily Digest Recipients
                                                        </div>
                                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                                            <Input
                                                                type="text"
                                                                defaultValue={u.digest_recipients || ''}
                                                                placeholder="officer2@agency.gov.in; supervisor@agency.gov.in"
                                                                className="h-8 text-xs"
                                                                id={`digest-${u.id}`}
                                                            />
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-8 shrink-0"
                                                                onClick={async () => {
                                                                    const input = document.getElementById(`digest-${u.id}`) as HTMLInputElement;
                                                                    if (!input) return;
                                                                    try {
                                                                        await userService.updateDigestRecipients(u.id, input.value);
                                                                        toast.success(`Digest recipients updated for ${u.username}`);
                                                                    } catch (err: any) {
                                                                        toast.error(err?.response?.data?.detail || 'Failed to update');
                                                                    }
                                                                }}
                                                            >
                                                                Save
                                                            </Button>
                                                        </div>
                                                        <p className="mt-1 text-[10px] text-muted-foreground/70">
                                                            Comma or semicolon separated. These emails receive this user&apos;s daily activity digest alongside their primary email.
                                                        </p>
                                                    </div>

                                                    <div>
                                                        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                                                            <Activity className="h-3.5 w-3.5" /> Recent Activity for @{u.username}
                                                        </div>
                                                        {activityLoading ? (
                                                            <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
                                                                <Loader2 className="h-3 w-3 animate-spin" /> Loading...
                                                            </div>
                                                        ) : userActivity.length === 0 ? (
                                                            <p className="text-xs text-muted-foreground/80">No activity recorded.</p>
                                                        ) : (
                                                            <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-border/50 bg-background/40 px-2 py-1">
                                                                {userActivity.map((a: any) => (
                                                                    <div key={a.id} className="flex items-center gap-3 border-b border-border/40 py-1.5 text-xs last:border-0">
                                                                        <span className="w-36 shrink-0 font-mono text-muted-foreground/80">
                                                                            {new Date(a.created_at).toLocaleString()}
                                                                        </span>
                                                                        <span className={cn(
                                                                            'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
                                                                            a.action === 'POST' ? 'bg-primary/10 text-primary' :
                                                                            a.action === 'GET' ? 'bg-muted text-muted-foreground' :
                                                                            a.action === 'DELETE' ? 'bg-red-500/10 text-red-400' :
                                                                            'bg-muted text-muted-foreground',
                                                                        )}>
                                                                            {a.action}
                                                                        </span>
                                                                        <span className="truncate text-foreground/80">{a.resource_type || a.activity_type}</span>
                                                                        {a.resource_id && (
                                                                            <span className="max-w-[200px] truncate font-mono text-muted-foreground/80">{a.resource_id}</span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Digest email */}
                            <div className="mt-5 rounded-xl border border-border bg-background/60 p-4">
                                <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
                                    <Mail className="h-4 w-4 text-primary" /> Email Activity Digest
                                </h3>
                                <p className="mb-3 text-xs text-muted-foreground">
                                    Send a summary of all user activity (searches, investigations, API calls) for the last 7 days.
                                </p>
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <Input
                                        type="email"
                                        placeholder="recipient@example.com"
                                        className="flex-1"
                                        value={digestEmail}
                                        onChange={e => setDigestEmail(e.target.value)}
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="shrink-0"
                                        disabled={digestSending || !digestEmail}
                                        onClick={async () => {
                                            if (!digestEmail) return;
                                            setDigestSending(true);
                                            try {
                                                await userService.sendDigest(digestEmail, 7);
                                                toast.success(`Digest sent to ${digestEmail}`);
                                            } catch (err: any) {
                                                toast.error(err?.response?.data?.detail || 'Failed to send digest');
                                            } finally {
                                                setDigestSending(false);
                                            }
                                        }}
                                    >
                                        {digestSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                                        Send Digest
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Create User Modal */}
                    {showModal && (
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                            <div className="bg-secondary/30 border border-border rounded-xl w-full max-w-md p-6 shadow-2xl">
                                <h2 className="text-xl font-bold mb-4">Add User</h2>
                                <form onSubmit={handleCreateUser} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">Username</label>
                                        <input
                                            type="text" required
                                            className="w-full bg-card border border-border rounded p-2 text-foreground"
                                            value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">Full Name (optional)</label>
                                        <input
                                            type="text"
                                            className="w-full bg-card border border-border rounded p-2 text-foreground"
                                            value={newUser.full_name} onChange={e => setNewUser({ ...newUser, full_name: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">Email</label>
                                        <input
                                            type="email" required
                                            className="w-full bg-card border border-border rounded p-2 text-foreground"
                                            value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">Password</label>
                                        <input
                                            type="password" required minLength={6}
                                            className="w-full bg-card border border-border rounded p-2 text-foreground"
                                            value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                                        />
                                        <p className="text-xs text-muted-foreground/80 mt-1">Min 6 characters. User can change it later via email OTP.</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">Role</label>
                                        <select
                                            className="w-full bg-card border border-border rounded p-2 text-foreground"
                                            value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                                        >
                                            <option value="officer">Officer — Full investigation access</option>
                                            <option value="analyst">Analyst — Full investigation access</option>
                                            <option value="trial">Trial — Workbench triage/trace and own cases only</option>
                                            {isSuper && (
                                                <>
                                                    <option value="admin">Admin — Full system access</option>
                                                    <option value="super_admin">Super Admin — All privileges</option>
                                                </>
                                            )}
                                        </select>
                                    </div>

                                    <div className="flex justify-end gap-3 mt-6">
                                        <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-muted-foreground hover:text-foreground">Cancel</button>
                                        <button type="submit" className="bg-primary hover:bg-brand-gold-hover text-primary-foreground px-4 py-2 rounded-lg">Create User</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Reset Password Modal */}
                    {resetPwUser && (
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                            <div className="bg-secondary/30 border border-border rounded-xl w-full max-w-md p-6 shadow-2xl">
                                <h2 className="text-xl font-bold mb-1 flex items-center gap-2">
                                    <Key className="w-5 h-5 text-amber-400" /> Reset Password
                                </h2>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Set a new password for <span className="font-mono text-foreground">@{resetPwUser.username}</span>.
                                </p>
                                <form onSubmit={handleResetPassword} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">New Password</label>
                                        <div className="relative">
                                            <input
                                                type={resetPwShow ? 'text' : 'password'}
                                                required minLength={6}
                                                className="w-full bg-card border border-border rounded p-2 pr-10 text-foreground focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                                                value={resetPwValue}
                                                onChange={e => setResetPwValue(e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setResetPwShow(!resetPwShow)}
                                                className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                                            >
                                                {resetPwShow ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                        <p className="text-xs text-muted-foreground/80 mt-1">Min 6 characters.</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">Confirm Password</label>
                                        <input
                                            type="password"
                                            required
                                            className="w-full bg-card border border-border rounded p-2 text-foreground focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                                            value={resetPwConfirm}
                                            onChange={e => setResetPwConfirm(e.target.value)}
                                        />
                                        {resetPwConfirm && resetPwValue !== resetPwConfirm && (
                                            <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                                        )}
                                    </div>
                                    <div className="flex justify-end gap-3 mt-6">
                                        <button
                                            type="button"
                                            onClick={() => setResetPwUser(null)}
                                            className="px-4 py-2 text-muted-foreground hover:text-foreground"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={resetPwSaving || resetPwValue.length < 6 || resetPwValue !== resetPwConfirm}
                                            className="bg-amber-600 hover:bg-amber-500 text-foreground px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
                                        >
                                            {resetPwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                                            Reset Password
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ═══════════════════ PROVIDERS TAB (super-admin) ═══════════════════ */}
            {activeTab === 'providers' && isSuper && (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                                <Plug className="w-5 h-5 text-primary" /> 3rd-Party Provider Catalog
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                Static spec + live usage for every external API the platform calls.
                                Counts flush from in-process buffer to Postgres every 30 seconds.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            {providerUsage?.as_of && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> {new Date(providerUsage.as_of).toLocaleTimeString()}
                                </span>
                            )}
                            <button
                                onClick={() => {
                                    loadProviderUsage();
                                    loadSanctionsCoverage();
                                }}
                                disabled={providerUsageLoading || sanctionsCoverageLoading}
                                className="px-3 py-1.5 bg-primary/10 text-primary border border-primary/30 rounded-lg hover:bg-primary/20 transition-colors flex items-center gap-2 text-sm"
                            >
                                {providerUsageLoading || sanctionsCoverageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                Refresh
                            </button>
                        </div>
                    </div>

                    {/* Summary chips */}
                    {providerUsage && (
                        <div className="grid grid-cols-4 gap-4">
                            <div className="bg-card border border-border rounded-xl p-4">
                                <div className="text-3xl font-bold text-foreground">
                                    {Object.keys(providerUsage.providers || {}).length}
                                </div>
                                <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Registered providers</div>
                            </div>
                            <div className="bg-card border border-border rounded-xl p-4">
                                <div className="text-3xl font-bold text-emerald-400">
                                    {Object.values(providerUsage.providers || {}).filter((p: any) => p.spec.is_active).length}
                                </div>
                                <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Active in cascade</div>
                            </div>
                            <div className="bg-card border border-border rounded-xl p-4">
                                <div className="text-3xl font-bold text-primary">
                                    {Object.values(providerUsage.providers || {}).filter((p: any) => (p.today?.total ?? 0) > 0).length}
                                </div>
                                <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Called today</div>
                            </div>
                            <div className="bg-card border border-border rounded-xl p-4">
                                <div className="text-3xl font-bold text-primary">
                                    {Object.values(providerUsage.providers || {}).reduce((acc: number, p: any) => acc + (p.today?.total ?? 0), 0).toLocaleString()}
                                </div>
                                <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Total calls today</div>
                            </div>
                        </div>
                    )}

                    {/* Arkham Cost Control — paid trial, preserve credits */}
                    {providerUsage?.arkham_controls && (() => {
                        const ac = providerUsage.arkham_controls;
                        const budgetPct = ac.credit_budget > 0
                            ? Math.min(100, Math.round((ac.credits_used_estimate / ac.credit_budget) * 100))
                            : null;
                        const overrideLabel = (ovr: boolean | null, def: boolean) =>
                            ovr === null ? `default · ${def ? 'on' : 'off'}` : 'runtime override';
                        return (
                            <div className="bg-card border border-amber-600/40 rounded-xl p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                                        <Zap className="w-4 h-4 text-amber-400" /> Arkham Cost Control
                                        <span className="text-xs font-normal text-muted-foreground">paid trial — preserve credits</span>
                                    </h3>
                                    {ac.credit_exhausted && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-600/20 text-red-400 border border-red-600/40">
                                            circuit open — Arkham suppressed
                                        </span>
                                    )}
                                </div>
                                {!ac.has_api_key ? (
                                    <div className="text-sm text-muted-foreground italic">No ARKHAM_API_KEY configured.</div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex items-center justify-between bg-background border border-border rounded-lg px-4 py-3">
                                                <div>
                                                    <div className="text-sm font-medium text-foreground">Attribution (lookup/batch)</div>
                                                    <div className="text-xs text-muted-foreground">Master switch · {overrideLabel(ac.runtime_overrides.enabled, ac.settings_defaults.enabled)}</div>
                                                </div>
                                                <button
                                                    onClick={() => handleArkhamControl({ enabled: !ac.enabled })}
                                                    disabled={arkhamSaving}
                                                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors disabled:opacity-40 ${ac.enabled ? 'bg-emerald-600/20 text-emerald-400 border-emerald-600/50' : 'bg-muted text-muted-foreground border-border'}`}
                                                >{ac.enabled ? 'ON' : 'OFF'}</button>
                                            </div>
                                            <div className="flex items-center justify-between bg-background border border-border rounded-lg px-4 py-3">
                                                <div>
                                                    <div className="text-sm font-medium text-foreground flex items-center gap-1">
                                                        <ArrowRightLeft className="w-3.5 h-3.5 text-amber-400" /> /transfers
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-600/20 text-amber-400">2 credits/row</span>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">The credit sink — enable per demo only</div>
                                                </div>
                                                <button
                                                    onClick={() => handleArkhamControl({ transfers_enabled: !ac.transfers_enabled })}
                                                    disabled={arkhamSaving || !ac.enabled}
                                                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors disabled:opacity-40 ${ac.transfers_enabled ? 'bg-amber-600/20 text-amber-400 border-amber-600/50' : 'bg-muted text-muted-foreground border-border'}`}
                                                >{ac.transfers_enabled ? 'ON (demo)' : 'OFF'}</button>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between text-xs mb-1">
                                                <span className="text-muted-foreground">Estimated credits used (this process)</span>
                                                <span className="text-foreground font-medium">
                                                    {(ac.credits_used_estimate ?? 0).toLocaleString()}{ac.credit_budget > 0 ? ` / ${ac.credit_budget.toLocaleString()}` : ' · no budget set'}
                                                </span>
                                            </div>
                                            {budgetPct !== null && (
                                                <div className="h-2 bg-background rounded-full overflow-hidden border border-border">
                                                    <div className={`h-full ${budgetPct >= 90 ? 'bg-red-500' : budgetPct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${budgetPct}%` }} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button onClick={() => handleArkhamControl({ reset_credit_estimate: true })} disabled={arkhamSaving} className="text-xs px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground disabled:opacity-40">Reset credit counter</button>
                                            <button onClick={() => handleArkhamControl({ clear_enabled_override: true, clear_transfers_override: true })} disabled={arkhamSaving} className="text-xs px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground disabled:opacity-40">Clear overrides (use env defaults)</button>
                                            {arkhamSaving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Cascade Explorer */}
                    <div className="bg-card border border-border rounded-xl p-5">
                        <h3 className="text-base font-semibold text-foreground flex items-center gap-2 mb-4">
                            <Workflow className="w-4 h-4 text-cyan-400" /> Cascade Explorer
                        </h3>
                        <div className="flex items-center gap-3 mb-4">
                            <select
                                value={cascadeChain}
                                onChange={(e) => setCascadeChain(e.target.value)}
                                className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-foreground"
                            >
                                {['ETH', 'BNB', 'MATIC', 'ARB', 'OP', 'AVAX', 'BASE', 'TRX', 'BTC', 'SOL', 'TON', 'XRP'].map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                            <select
                                value={cascadeCapability}
                                onChange={(e) => setCascadeCapability(e.target.value)}
                                className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-foreground"
                            >
                                <option value="entity">entity (attribution)</option>
                                <option value="tx_history">tx_history</option>
                                <option value="tx_detail">tx_detail (transaction detail)</option>
                                <option value="risk_flags">risk_flags</option>
                                <option value="sanctions">sanctions</option>
                                <option value="osint">osint</option>
                                <option value="graph">graph</option>
                            </select>
                            <span className="text-xs text-muted-foreground">
                                Order = which provider is asked first ↓
                            </span>
                        </div>
                        {cascadeResult ? (
                            cascadeResult.providers?.length === 0 ? (
                                <div className="text-sm text-muted-foreground italic">
                                    No active providers cover this (chain, capability) pair.
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {cascadeResult.providers.map((p: any, idx: number) => (
                                        <div key={p.name} className="flex items-center gap-3 p-2 rounded-lg hover:bg-background/50">
                                            <span className="font-mono text-xs px-2 py-0.5 rounded bg-primary/20 text-primary w-12 text-center">
                                                P{p.cascade_priority}
                                            </span>
                                            <span className="text-sm font-medium text-foreground w-44">{p.display_name}</span>
                                            <span className="text-xs text-muted-foreground">{p.cascade_role}</span>
                                            <span className="text-xs text-muted-foreground ml-auto">{p.quota_summary}</span>
                                            {p.typical_hit_rate_pct !== null && p.typical_hit_rate_pct !== undefined && (
                                                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400">
                                                    ~{p.typical_hit_rate_pct}% hit
                                                </span>
                                            )}
                                            {idx < cascadeResult.providers.length - 1 && (
                                                <span className="text-muted-foreground">↓</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )
                        ) : (
                            <div className="text-sm text-muted-foreground italic">Select chain + capability...</div>
                        )}
                    </div>

                    {/* Sanctions Freshness Table (SANC02) */}
                    <div className="bg-card border border-border rounded-xl">
                        <div className="flex items-center justify-between p-4 border-b border-border">
                            <div>
                                <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-primary" /> Sanctions Lists Coverage & Freshness
                                </h3>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Periodic ingest schedule, source row counts, and freshness SLA tracking.
                                </p>
                            </div>
                            {sanctionsCoverage?.summary && (
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="text-muted-foreground">
                                        Total: <strong className="text-foreground">{sanctionsCoverage.summary.total_rows.toLocaleString()}</strong> rows
                                    </span>
                                    <span className={`px-2 py-0.5 rounded font-medium ${
                                        sanctionsCoverage.summary.loaded && !sanctionsCoverage.summary.stale
                                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                            : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                    }`}>
                                        {sanctionsCoverage.summary.loaded ? (sanctionsCoverage.summary.stale ? 'Stale' : 'Operational') : 'Unloaded'}
                                    </span>
                                </div>
                            )}
                        </div>
                        {sanctionsCoverageLoading && !sanctionsCoverage ? (
                            <div className="p-8 text-center text-muted-foreground">
                                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                                Loading sanctions lists coverage...
                            </div>
                        ) : sanctionsCoverage?.sources ? (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-xs uppercase text-muted-foreground border-b border-border">
                                        <th className="text-left px-4 py-3">Source</th>
                                        <th className="text-right px-4 py-3">Rows</th>
                                        <th className="text-left px-4 py-3">Last Refreshed</th>
                                        <th className="text-right px-4 py-3">Age</th>
                                        <th className="text-center px-4 py-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border font-mono text-xs">
                                    {sanctionsCoverage.sources.map((src: any) => {
                                        const status = src.status || (src.stale ? 'stale' : src.rows === 0 ? 'empty' : 'fresh');
                                        const badgeClass =
                                            status === 'fresh'
                                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                                : status === 'stale'
                                                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                                : 'bg-rose-500/10 text-rose-400 border border-rose-500/20';
                                        return (
                                            <tr key={src.source} className="hover:bg-muted/30">
                                                <td className="px-4 py-3 font-sans font-medium text-foreground">
                                                    {src.source}
                                                </td>
                                                <td className="text-right px-4 py-3">
                                                    {src.rows.toLocaleString()}
                                                </td>
                                                <td className="text-left px-4 py-3 font-sans text-muted-foreground">
                                                    {src.last_refreshed_at ? new Date(src.last_refreshed_at).toLocaleString() : 'Never'}
                                                </td>
                                                <td className="text-right px-4 py-3 text-muted-foreground">
                                                    {src.age_days !== null && src.age_days !== undefined
                                                        ? `${src.age_days.toFixed(1)}d (SLA: ${src.sla_days}d)`
                                                        : `SLA: ${src.sla_days}d`}
                                                </td>
                                                <td className="text-center px-4 py-3">
                                                    <span className={`inline-block px-2 py-0.5 rounded font-sans uppercase text-[10px] tracking-wider font-semibold ${badgeClass}`}>
                                                        {status}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="p-8 text-center text-muted-foreground">
                                No sanctions coverage data available.
                            </div>
                        )}
                    </div>

                    {/* Provider table */}
                    <div className="bg-card border border-border rounded-xl">
                        <div className="flex items-center justify-between p-4 border-b border-border">
                            <h3 className="text-base font-semibold text-foreground">Per-Provider Usage</h3>
                            <div className="flex items-center gap-2">
                                <select
                                    value={providerFilter}
                                    onChange={(e) => setProviderFilter(e.target.value as any)}
                                    className="px-2 py-1 bg-background border border-border rounded text-xs text-foreground"
                                >
                                    <option value="all">All</option>
                                    <option value="active">Active only</option>
                                    <option value="with_calls_today">Called today</option>
                                </select>
                                <select
                                    value={providerSort}
                                    onChange={(e) => setProviderSort(e.target.value as any)}
                                    className="px-2 py-1 bg-background border border-border rounded text-xs text-foreground"
                                >
                                    <option value="today_calls">Sort: today's calls</option>
                                    <option value="free_tier_pct">Sort: % of free tier</option>
                                    <option value="name">Sort: name</option>
                                </select>
                            </div>
                        </div>
                        {providerUsageLoading && !providerUsage ? (
                            <div className="p-12 text-center text-muted-foreground">
                                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                                Loading provider usage...
                            </div>
                        ) : providerUsage ? (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-xs uppercase text-muted-foreground border-b border-border">
                                        <th className="text-left px-4 py-3">Provider</th>
                                        <th className="text-left px-4 py-3">Capability</th>
                                        <th className="text-left px-4 py-3">Chains</th>
                                        <th className="text-right px-4 py-3">Today</th>
                                        <th className="text-right px-4 py-3" title="Sum of calls over the last 7 days (today included)">Last 7d</th>
                                        <th className="text-right px-4 py-3" title="Sum of calls over the last 30 days (today included)">Last 30d</th>
                                        <th className="text-right px-4 py-3">Est. quota</th>
                                        <th className="text-right px-4 py-3">% free tier</th>
                                        <th className="text-left px-4 py-3">Free tier</th>
                                        <th className="text-left px-4 py-3">Status</th>
                                        <th className="text-left px-4 py-3">Breaker</th>
                                        <th className="text-left px-4 py-3" title="One-shot probe with a known fixture address. Verifies key + reachability.">Test</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.values(providerUsage.providers || {})
                                        .filter((p: any) => {
                                            if (providerFilter === 'active') return p.spec.is_active;
                                            if (providerFilter === 'with_calls_today') return (p.today?.total ?? 0) > 0;
                                            return true;
                                        })
                                        .sort((a: any, b: any) => {
                                            if (providerSort === 'name') return a.spec.name.localeCompare(b.spec.name);
                                            if (providerSort === 'free_tier_pct') {
                                                return (b.today?.estimated_quota_pct_of_free_tier ?? 0) - (a.today?.estimated_quota_pct_of_free_tier ?? 0);
                                            }
                                            return (b.today?.total ?? 0) - (a.today?.total ?? 0);
                                        })
                                        .map((p: any) => {
                                            const pct = p.today?.estimated_quota_pct_of_free_tier;
                                            const tone = pct === null || pct === undefined
                                                ? 'text-muted-foreground'
                                                : pct >= 80 ? 'text-red-400'
                                                : pct >= 50 ? 'text-amber-400'
                                                : 'text-emerald-400';
                                            return (
                                                <tr key={p.spec.name} className="border-b border-border hover:bg-background/30">
                                                    <td className="px-4 py-3">
                                                        <div className="font-medium text-foreground">{p.spec.display_name}</div>
                                                        <div className="text-xs text-muted-foreground font-mono">{p.spec.name}</div>
                                                    </td>
                                                    <td className="px-4 py-3 text-xs">
                                                        {p.spec.capabilities.map((c: string) => (
                                                            <span key={c} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-primary/10 text-primary">{c}</span>
                                                        ))}
                                                    </td>
                                                    <td className="px-4 py-3 text-xs text-muted-foreground">
                                                        {p.spec.chains.length === 0 ? <span className="italic">all</span>
                                                            : p.spec.chains.length > 4 ? `${p.spec.chains.slice(0, 3).join(', ')} +${p.spec.chains.length - 3}`
                                                                : p.spec.chains.join(', ')}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-mono">
                                                        {(p.today?.total ?? 0).toLocaleString()}
                                                        {p.today?.failed > 0 && (
                                                            <span className="text-xs text-red-400 ml-1">({p.today.failed} fail)</span>
                                                        )}
                                                    </td>
                                                    {/* 7d + 30d aggregates from history[] (history[0] = today) */}
                                                    <td className="px-4 py-3 text-right font-mono text-muted-foreground">
                                                        {(((p.history || []).slice(0, 7) as any[]).reduce((s, r) => s + (r.total || 0), 0)).toLocaleString()}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-mono text-muted-foreground">
                                                        {(((p.history || []).slice(0, 30) as any[]).reduce((s, r) => s + (r.total || 0), 0)).toLocaleString()}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-mono text-muted-foreground">
                                                        {(p.today?.estimated_quota_units ?? 0).toLocaleString()} {p.spec.quota_model !== 'internal' && p.spec.quota_model !== 'unlimited' ? p.spec.quota_model : ''}
                                                    </td>
                                                    <td className={`px-4 py-3 text-right font-mono ${tone}`}>
                                                        {pct === null || pct === undefined ? '—' : `${pct.toFixed(1)}%`}
                                                    </td>
                                                    <td className="px-4 py-3 text-xs text-muted-foreground">
                                                        {p.spec.quota_summary}
                                                        {providerUsage.daily_quota?.[p.spec.name] && (() => {
                                                            const dq = providerUsage.daily_quota[p.spec.name];
                                                            const rem = dq.remaining_today ?? dq.remaining ?? 0;
                                                            const lim = dq.daily_limit ?? dq.limit ?? 0;
                                                            return (
                                                                <div
                                                                    className="font-mono mt-1 text-foreground"
                                                                    data-testid={`daily-quota-${p.spec.name}`}
                                                                    title="Persisted daily ledger, UTC day. A hard stop: at the limit no call is made."
                                                                >
                                                                    used today {dq.used_today} / {lim}, remaining {rem} (UTC)
                                                                    {rem <= 0 && lim > 0 && (
                                                                        <span className="ml-1.5 px-1 py-0.5 rounded bg-red-500/20 text-red-400 text-[10px]">exhausted</span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                        {providerUsage.monthly_quota?.[p.spec.name] && (
                                                            <div
                                                                className="font-mono mt-1 text-foreground"
                                                                data-testid={`monthly-quota-${p.spec.name}`}
                                                                title="Persisted monthly ledger, UTC month."
                                                            >
                                                                used this month {(providerUsage.monthly_quota[p.spec.name].used_today ?? providerUsage.monthly_quota[p.spec.name].used_this_month ?? 0).toLocaleString()} / {providerUsage.monthly_quota[p.spec.name].limit > 0 ? providerUsage.monthly_quota[p.spec.name].limit.toLocaleString() : (p.spec.quota_limit_free_tier ? p.spec.quota_limit_free_tier.toLocaleString() : 'unlimited')},
                                                                remaining {providerUsage.monthly_quota[p.spec.name].limit > 0 ? providerUsage.monthly_quota[p.spec.name].remaining.toLocaleString() : (p.spec.quota_limit_free_tier ? (p.spec.quota_limit_free_tier - (providerUsage.monthly_quota[p.spec.name].used_today ?? providerUsage.monthly_quota[p.spec.name].used_this_month ?? 0)).toLocaleString() : 'unlimited')} (UTC)
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-xs">
                                                        {p.spec.is_active ? (
                                                            <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">active</span>
                                                        ) : (
                                                            <span className="px-1.5 py-0.5 rounded bg-zinc-500/20 text-zinc-400">opt-in</span>
                                                        )}
                                                        {p.spec.auth_env && (
                                                            <div className="text-xs text-muted-foreground font-mono mt-1">{p.spec.auth_env}</div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-xs">
                                                        {p.circuit_state?.open ? (
                                                            <div className="space-y-1">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-semibold">OPEN</span>
                                                                    <button
                                                                        onClick={() => resetProviderCircuit(p.spec.name)}
                                                                        className="px-1.5 py-0.5 rounded bg-zinc-700 hover:bg-zinc-600 text-zinc-200 text-xs"
                                                                        title="Manually close the breaker (e.g. after upgrading the plan)"
                                                                    >
                                                                        reset
                                                                    </button>
                                                                </div>
                                                                {p.circuit_state.trip_reason && (
                                                                    <div className="text-muted-foreground text-xs">{p.circuit_state.trip_reason}</div>
                                                                )}
                                                                {p.circuit_state.next_probe_at && (
                                                                    <div className="text-muted-foreground text-xs">
                                                                        retry at {new Date(p.circuit_state.next_probe_at * 1000).toLocaleTimeString()}
                                                                    </div>
                                                                )}
                                                                {p.circuit_state.suppressed_calls_since_open > 0 && (
                                                                    <div className="text-muted-foreground text-xs">
                                                                        {p.circuit_state.suppressed_calls_since_open} calls suppressed
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : p.circuit_state?.recent_failures_in_window > 0 ? (
                                                            <span
                                                                className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400"
                                                                title={`${p.circuit_state.recent_failures_in_window}/${p.circuit_state.threshold} failures in ${p.circuit_state.window_s}s`}
                                                            >
                                                                {p.circuit_state.recent_failures_in_window}/{p.circuit_state.threshold} fails
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted-foreground">closed</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-xs">
                                                        {(() => {
                                                            const result = testResults[p.spec.name];
                                                            const inFlight = testingProvider === p.spec.name;
                                                            return (
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        onClick={() => testProvider(p.spec.name)}
                                                                        disabled={inFlight}
                                                                        className="px-1.5 py-0.5 rounded bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-zinc-200 text-xs"
                                                                        title={p.spec.name === 'chainstack_eth'
                                                                            ? 'Spends 1 request (eth_blockNumber) against the private Ethereum node'
                                                                            : providerUsage.daily_quota?.[p.spec.name]
                                                                            ? 'Spends one item of the daily allowance (10 a day): probes this provider with a known-labelled fixture address'
                                                                            : 'Probe this provider with a known-labelled fixture address'}
                                                                    >
                                                                        {inFlight ? 'testing…' : 'test'}
                                                                    </button>
                                                                    {result && !inFlight && (
                                                                        <span
                                                                            className={result.ok
                                                                                ? 'px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400'
                                                                                : 'px-1.5 py-0.5 rounded bg-red-500/20 text-red-400'}
                                                                            title={result.result?.summary
                                                                                || result.error
                                                                                || result.result?.skipped
                                                                                || ''}
                                                                        >
                                                                            {result.ok ? `${result.duration_ms} ms` : 'fail'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        ) : null}
                    </div>

                    {/* Buffer & guard stats */}
                    {providerUsage && (
                        <div className="grid grid-cols-2 gap-4 text-xs">
                            <div className="bg-card border border-border rounded-xl p-4">
                                <h4 className="font-semibold text-foreground mb-2">In-process counter buffer</h4>
                                <div className="text-muted-foreground">
                                    {providerUsage.buffer_pending_rows} unflushed (provider, day) row(s).
                                    Flush every 30 s.
                                </div>
                            </div>
                            <div className="bg-card border border-border rounded-xl p-4">
                                <h4 className="font-semibold text-foreground mb-2">Negative-cache guard</h4>
                                <div className="text-muted-foreground space-y-0.5">
                                    <div>{providerUsage.guard_stats?.negative_cache_active ?? 0} active entries</div>
                                    {Object.entries(providerUsage.guard_stats?.by_provider || {}).map(([k, v]: any) => (
                                        <div key={k} className="font-mono">{k}: {v}</div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ═══════════════════ CLIENT ACTIVITY TAB (super-admin) ═══════════════════ */}
            {/* ═══════════════════ SOURCES TAB ═══════════════════ */}
            {activeTab === 'sources' && (isSuper || canReviewSources) && (
                <SourcesTab canViewManifest={isSuper} canReview={canReviewSources} canApprove={canAdminTools} />
            )}

            {activeTab === 'activity' && isSuper && (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                                <ListTree className="w-5 h-5 text-primary" /> Client Activity
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                Cross-user API audit trail. Every authenticated request is logged with user, endpoint, status, and IP.
                            </p>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-3 bg-card border border-border rounded-xl p-4">
                        <div className="flex items-center gap-2">
                            <label className="text-xs text-muted-foreground uppercase tracking-wider">Window</label>
                            <select
                                value={activityWindow}
                                onChange={(e) => setActivityWindow(parseInt(e.target.value, 10))}
                                className="px-2 py-1.5 bg-background border border-border rounded text-sm text-foreground"
                            >
                                <option value={15}>Last 15 min</option>
                                <option value={60}>Last 1 hour</option>
                                <option value={1440}>Last 24 hours</option>
                                <option value={10080}>Last 7 days</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-xs text-muted-foreground uppercase tracking-wider">Endpoint</label>
                            <input
                                type="text"
                                placeholder="e.g. investigation"
                                value={activityEndpoint}
                                onChange={(e) => setActivityEndpoint(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') loadActivityLog(); }}
                                className="px-2 py-1.5 bg-background border border-border rounded text-sm text-foreground w-48 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-xs text-muted-foreground uppercase tracking-wider">User ID</label>
                            <input
                                type="text"
                                placeholder="all"
                                value={activityUserFilter}
                                onChange={(e) => setActivityUserFilter(e.target.value.replace(/\D/g, ''))}
                                onKeyDown={(e) => { if (e.key === 'Enter') loadActivityLog(); }}
                                className="px-2 py-1.5 bg-background border border-border rounded text-sm text-foreground w-24 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                            />
                        </div>
                        <button
                            onClick={loadActivityLog}
                            disabled={activityLogLoading}
                            className="px-3 py-1.5 bg-primary/10 text-primary border border-primary/30 rounded-lg hover:bg-primary/20 transition-colors flex items-center gap-2 text-sm ml-auto"
                        >
                            {activityLogLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                            Refresh
                        </button>
                    </div>

                    {/* Activity table */}
                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs text-muted-foreground uppercase bg-background border-b border-border">
                                    <tr>
                                        <th className="px-4 py-3">Time</th>
                                        <th className="px-4 py-3">User</th>
                                        <th className="px-4 py-3">Method</th>
                                        <th className="px-4 py-3">Endpoint</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3">IP</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800">
                                    {activityLogLoading && activityRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                                                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading activity...
                                            </td>
                                        </tr>
                                    ) : activityRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground/80">
                                                No activity in the selected window.
                                            </td>
                                        </tr>
                                    ) : (
                                        activityRows.map((a) => (
                                            <tr key={a.id} className="hover:bg-background/30">
                                                <td className="px-4 py-2.5 text-muted-foreground/80 font-mono text-xs whitespace-nowrap">
                                                    {new Date(a.created_at).toLocaleString()}
                                                </td>
                                                <td className="px-4 py-2.5 text-foreground/80">
                                                    {a.username || (a.user_id != null ? `#${a.user_id}` : 'guest')}
                                                </td>
                                                <td className="px-4 py-2.5">
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                                                        a.action === 'POST' ? 'bg-primary/10 text-primary' :
                                                        a.action === 'GET' ? 'bg-slate-500/10 text-muted-foreground' :
                                                        a.action === 'DELETE' ? 'bg-red-500/10 text-red-400' :
                                                        a.action === 'PATCH' || a.action === 'PUT' ? 'bg-amber-500/10 text-amber-400' :
                                                        'bg-slate-500/10 text-muted-foreground'
                                                    }`}>
                                                        {a.action || '—'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2.5 text-foreground/80 font-mono text-xs truncate max-w-[360px]">
                                                    {a.resource_id || a.resource_type || a.activity_type || '—'}
                                                </td>
                                                <td className="px-4 py-2.5">
                                                    {a.status_code != null ? (
                                                        <span className={`font-mono text-xs ${
                                                            a.status_code >= 200 && a.status_code < 300 ? 'text-emerald-400' :
                                                            a.status_code >= 400 ? 'text-red-400' :
                                                            'text-muted-foreground'
                                                        }`}>
                                                            {a.status_code}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted-foreground/60">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2.5 text-muted-foreground/80 font-mono text-xs">
                                                    {a.ip_address || '—'}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {activityRows.length > 0 && (
                            <div className="px-4 py-2 text-xs text-muted-foreground border-t border-border">
                                {activityRows.length} row{activityRows.length === 1 ? '' : 's'} shown.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ═══════════════════ WHAT'S NEW TAB ═══════════════════ */}
            {activeTab === 'thresholds' && <DetectionThresholdsTab />}

            {activeTab === 'llmcost' && canLlmCost && <LlmCostTab />}

            {activeTab === 'scoreboard' && isSuper && <ScoreboardTab />}

            {activeTab === 'whatsnew' && (
                <div className="space-y-8">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div>
                            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
                                <Zap className="w-6 h-6 text-yellow-500" /> Platform Updates
                            </h1>
                            <p className="text-sm text-muted-foreground mt-1">
                                {canManageUsers
                                    ? <>Version changelog for CryptoFlow. On publish, a newsletter email goes to{' '}
                                        <span className="font-mono text-foreground/80">{newsletterTo}</span>.</>
                                    : 'Version changelog for CryptoFlow — what changed in recent releases.'}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={loadProductUpdates}
                            disabled={productUpdatesLoading}
                            className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-border hover:bg-muted text-muted-foreground"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${productUpdatesLoading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>

                    {canManageUsers && (
                        <div className="bg-secondary/30 border border-border rounded-xl p-5 space-y-4">
                            <div className="flex items-center justify-between gap-2">
                                <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-primary" />
                                    Publish a product update
                                </h2>
                                <span className="text-[11px] text-muted-foreground">Admin only · emails {newsletterTo}</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="md:col-span-2">
                                    <label className="block text-xs text-muted-foreground mb-1">Title</label>
                                    <input
                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                        value={newUpdateForm.title}
                                        onChange={(e) => setNewUpdateForm({ ...newUpdateForm, title: e.target.value })}
                                        placeholder="e.g. Fusion Canvas multi-seed layout"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-muted-foreground mb-1">Version label</label>
                                    <input
                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
                                        value={newUpdateForm.version_label}
                                        onChange={(e) => setNewUpdateForm({ ...newUpdateForm, version_label: e.target.value })}
                                        placeholder="e.g. 2026-08-06 or v6.2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-muted-foreground mb-1">Category</label>
                                    <select
                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                        value={newUpdateForm.category}
                                        onChange={(e) => setNewUpdateForm({ ...newUpdateForm, category: e.target.value })}
                                    >
                                        <option value="feature">Feature</option>
                                        <option value="quality">Quality</option>
                                        <option value="fix">Fix</option>
                                        <option value="security">Security</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-xs text-muted-foreground mb-1">Summary</label>
                                    <textarea
                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm min-h-[64px]"
                                        value={newUpdateForm.summary}
                                        onChange={(e) => setNewUpdateForm({ ...newUpdateForm, summary: e.target.value })}
                                        placeholder="One or two sentences describing the release"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-xs text-muted-foreground mb-1">
                                        Highlights (one per line; optional <span className="font-mono">Label: detail</span>)
                                    </label>
                                    <textarea
                                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm min-h-[100px] font-mono"
                                        value={newUpdateForm.bulletsText}
                                        onChange={(e) => setNewUpdateForm({ ...newUpdateForm, bulletsText: e.target.value })}
                                        placeholder={"Continue on Target Chain: Bridge hits open a workbench on the destination chain\nDEX Swap Labels: Edges show exact token conversions"}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={newUpdateForm.publishAndEmail}
                                        onChange={(e) =>
                                            setNewUpdateForm({ ...newUpdateForm, publishAndEmail: e.target.checked })
                                        }
                                    />
                                    Publish now and email {newsletterTo}
                                </label>
                                <button
                                    type="button"
                                    disabled={updateSaving || !newUpdateForm.title.trim()}
                                    onClick={handleCreateProductUpdate}
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
                                >
                                    {updateSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                                    {newUpdateForm.publishAndEmail ? 'Publish & email' : 'Save draft'}
                                </button>
                            </div>
                        </div>
                    )}

                    {productUpdatesLoading && productUpdates.length === 0 ? (
                        <div className="flex items-center gap-2 text-muted-foreground text-sm py-12 justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading updates…
                        </div>
                    ) : productUpdates.length === 0 ? (
                        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-xl p-8 text-center">
                            No product updates yet. Admins can publish the first entry above.
                        </div>
                    ) : (
                        productUpdates.map((u) => (
                            <div
                                key={u.id}
                                className={`bg-secondary/20 border rounded-xl p-6 relative overflow-hidden ${
                                    u.status === 'draft' ? 'border-amber-500/40' : 'border-border'
                                }`}
                            >
                                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                                    <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                                        {u.category === 'quality' || u.category === 'fix' ? (
                                            <Shield className="w-5 h-5 text-emerald-400 shrink-0" />
                                        ) : u.category === 'security' ? (
                                            <Lock className="w-5 h-5 text-red-400 shrink-0" />
                                        ) : (
                                            <ArrowRightLeft className="w-5 h-5 text-primary shrink-0" />
                                        )}
                                        {u.title}
                                    </h2>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {u.version_label && (
                                            <span className="bg-muted text-muted-foreground text-xs font-mono font-semibold px-2.5 py-1 rounded-full border border-border">
                                                {u.version_label}
                                            </span>
                                        )}
                                        {u.status === 'draft' && (
                                            <span className="bg-amber-500/15 text-amber-400 text-xs font-semibold px-2.5 py-1 rounded-full border border-amber-500/30">
                                                Draft
                                            </span>
                                        )}
                                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${categoryBadge(u.category)}`}>
                                            {categoryLabel(u.category)}
                                        </span>
                                    </div>
                                </div>

                                <div className="text-sm text-muted-foreground mb-4 flex flex-wrap items-center gap-x-4 gap-y-1">
                                    <span className="inline-flex items-center gap-2">
                                        <Clock className="w-4 h-4" /> Released: {formatReleased(u.released_at || u.published_at)}
                                    </span>
                                    {canManageUsers && u.email_status && (
                                        <span className="text-xs">
                                            Email:{' '}
                                            <span className="font-mono">
                                                {u.email_status}
                                                {u.email_recipient ? ` → ${u.email_recipient}` : ''}
                                            </span>
                                        </span>
                                    )}
                                </div>

                                <div className="space-y-4 text-foreground/80">
                                    {u.summary && <p className="leading-relaxed">{u.summary}</p>}
                                    {(u.bullets || []).length > 0 && (
                                        <>
                                            <h3 className="text-foreground font-semibold mt-2 mb-2">What's Included:</h3>
                                            <ul className="list-disc list-inside space-y-2 ml-2">
                                                {u.bullets.map((b, i) => (
                                                    <li key={i}>
                                                        {b.label ? (
                                                            <>
                                                                <strong className="text-primary">{b.label}:</strong> {b.text}
                                                            </>
                                                        ) : (
                                                            b.text
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </div>

                                {canManageUsers && (
                                    <div className="mt-5 pt-4 border-t border-border flex flex-wrap gap-2">
                                        {u.status === 'draft' && (
                                            <button
                                                type="button"
                                                disabled={updateSaving}
                                                onClick={() => handlePublishUpdate(u.id)}
                                                className="text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground disabled:opacity-50"
                                            >
                                                Publish & email
                                            </button>
                                        )}
                                        {u.status === 'published' && (
                                            <button
                                                type="button"
                                                disabled={updateSaving}
                                                onClick={() => handleResendUpdateEmail(u.id)}
                                                className="text-xs px-3 py-1.5 rounded-md border border-border hover:bg-muted disabled:opacity-50"
                                            >
                                                Resend email
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            disabled={updateSaving}
                                            onClick={() => handleDeleteUpdate(u.id, u.title)}
                                            className="text-xs px-3 py-1.5 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {confirmDialog}
        </div>
    );
};
